/*

  Usage:

  const Page = () => {
    const buttonRef = useRef(null)
    const [ isVisible ] = useVisibility

    if (isVisible) {
      sendAnalyticData()
    }

    return (
      <Fragment>
        <div>
          <Text />
          <Text />
        <div>
        <button ref={buttonRef}>Click me</button>
      </Fragment>
    )
  }

  A hook is used to understand whether a user sees an dom element.

  Example:
    We have a banner on the page and it’s located at the very bottom of the page,
    to see it the page needs to be scrolled.
    We want to send analytics when the user sees this banner.
 */

import { useCallback, useEffect, useState } from 'react'
import { throttle } from 'lodash'
import getElementInViewport from 'common/helpers/getters/getElementInViewport'
import useEventListener from 'sb-hooks/useEventListener'


const useVisibility = (elementRef) => {
  const [ isVisible, setIsVisible ] = useState(false)

  const handler = useCallback(throttle(() => {
    const isVisible = Boolean(elementRef.current) && getElementInViewport(elementRef.current)

    setIsVisible(isVisible)
  }, 150), [ elementRef ])

  const removeScrollListener = useEventListener('scroll', handler)
  const removeResizeListener = useEventListener('resize', handler)

  const removeListeners = useCallback(() => {
    removeScrollListener()
    removeResizeListener()
  }, [ removeScrollListener, removeResizeListener ])

  useEffect(() => {
    const isVisible = Boolean(elementRef.current) && getElementInViewport(elementRef.current)

    setIsVisible(isVisible)
  }, [ elementRef, removeListeners ])

  return [ isVisible, removeListeners ]
}


export default useVisibility
